import React, { useContext } from 'react';
import { StaticContext } from '@sm/webassets/StaticContext';
import { generateMetricsAttribute } from '@sm/metrics';
import { createURL } from '@sm/utils';
import { Button } from '@wds/button';
import { IconArrowRight } from '@wds/icons';
import { Grid } from '@wds/grid';
import { Box } from '@wds/box';
import { Typography } from '@wds/typography';
import { getSrc } from '~app/helpers/assetLoading';
import {
  treatment1Create,
  treatment1Take,
  treatment2Create,
  treatment2Take,
  treatment3Create,
  treatment3Take,
} from '~static/images/optionalPaths';
import useStyles from './useStyles';
import ColoredLogoWithText from '~app/components/surveyThanksLogo/ColoredLogoWithText';
import { OptionalPathsCopyType } from '~helpers/types';

// Copy to be translated when these experimental endpages are exposed to all locales
const COPY: OptionalPathsCopyType = {
  banner: 'Thank you for taking this survey.',
  header: 'Discover what else you can do with SurveyMonkey',
  creatorAlt: 'Survey creator',
  creatorHeader: 'Create unlimited forms and surveys for free',
  creatorSubHeader:
    'Send surveys to anyone, from friends to customers. Use insights to make better, data-driven decisions.',
  creatorCTA: 'Create surveys',
  takerAlt: 'Survey taker',
  takerHeader: 'Take more surveys and help charities',
  takerSubHeader:
    'With each survey, we’ll donate $0.50 to a participating charity. You could also win $100 gift cards.',
  takerCTA: 'Take more surveys',
};

function OptionalPaths({ utSource2 = 'OptionalPaths' }: { utSource2: string }): React.ReactElement {
  const utSource = 'survey-thanks';
  const utSource3SignUp = 'DesktopSignUp';
  const utSource3Contribute = 'DesktopContribute';

  const {
    smParams: { surveyId, collectorId, respondentId },
    templateType,
  } = useContext(StaticContext);

  const { card, headerText, leftContent, pathImage, pageContent } = useStyles();

  let leftImage = '';
  let rightImage = '';

  if (utSource2 === 'OptionalPathDesktop1') {
    leftImage = treatment1Create;
    rightImage = treatment1Take;
  } else if (utSource2 === 'OptionalPathDesktop2' || utSource2 === 'optionalPathOriginal') {
    leftImage = treatment2Create;
    rightImage = treatment2Take;
  } else if (utSource2 === 'OptionalPathDesktop3') {
    leftImage = treatment3Create;
    rightImage = treatment3Take;
  }

  return (
    <>
      <ColoredLogoWithText
        isThanksText={utSource2 === 'optionalPathOriginal'}
        utSource={utSource}
        utSource2={utSource2}
        amplitudeEvent="endpage user interaction"
      />
      <Box pt={8} pb={8} pr={4} pl={4} className={pageContent}>
        <Typography variant="pageTitle" weight="medium" align="center" component="h1">
          {COPY.header}
        </Typography>
        <Grid>
          <Grid.Item xl={6} lg={6} md={6} sm={12} xs={12}>
            <Box display="flex" className={leftContent}>
              <Box
                pb={8}
                pt={9}
                pr={6}
                pl={6}
                className={card}
                display="flex"
                flexJustify="flex-start"
                flexAlign="flex-start"
                flexDirection="column"
              >
                <img src={getSrc(leftImage)} alt={COPY.creatorAlt} className={pathImage} />
                <Box pt={6} pb={5}>
                  <Box pb={5}>
                    <Box pb={4} className={headerText}>
                      <Typography variant="cardTitle" weight="medium" component="h2">
                        {COPY.creatorHeader}
                      </Typography>
                    </Box>
                    <Typography component="p">{COPY.creatorSubHeader}</Typography>
                  </Box>
                  <Button
                    href={createURL('/sign-up', {
                      ut_source: utSource,
                      ut_source2: utSource2,
                      ut_source3: utSource3SignUp,
                      ut_ctatext: COPY.creatorCTA,
                    })}
                    data-sm-metrics={generateMetricsAttribute({
                      data: {
                        amplitudeEvent: 'optional paths survey creator cta',
                        itemSelected: 'button',
                        templateType,
                        surveyId,
                        collectorId,
                        respondentId,
                        log: {
                          message: `${utSource}-${utSource2}-${utSource3SignUp}`,
                        },
                      },
                    })}
                  >
                    {COPY.creatorCTA}
                    <Box display="inline" ml={4}>
                      <IconArrowRight title="arrow icon" />
                    </Box>
                  </Button>
                </Box>
              </Box>
            </Box>
          </Grid.Item>
          <Grid.Item xl={6} lg={6} md={6} sm={12} xs={12}>
            <Box
              pb={8}
              pt={9}
              pr={6}
              pl={6}
              className={card}
              display="flex"
              flexAlign="flex-start"
              flexDirection="column"
            >
              <img src={getSrc(rightImage)} alt={COPY.takerAlt} className={pathImage} />
              <Box pt={6} pb={5}>
                <Box pb={5}>
                  <Box pb={4} className={headerText}>
                    <Typography variant="cardTitle" weight="medium" component="h2">
                      {COPY.takerHeader}
                    </Typography>
                  </Box>
                  <Typography component="p">{COPY.takerSubHeader}</Typography>
                </Box>
                <Button
                  href={createURL('https://contribute.surveymonkey.com/sign-up/create-account', {
                    ut_source: utSource,
                    ut_source2: utSource2,
                    ut_source3: utSource3Contribute,
                    ut_ctatext: COPY.takerCTA,
                  })}
                  data-sm-metrics={generateMetricsAttribute({
                    data: {
                      amplitudeEvent: 'optional paths contribute cta',
                      itemSelected: 'button',
                      templateType,
                      surveyId,
                      collectorId,
                      respondentId,
                      log: {
                        message: `${utSource}-${utSource2}-${utSource3Contribute}`,
                      },
                    },
                  })}
                  variant="secondary"
                >
                  {COPY.takerCTA}
                  <Box display="inline" ml={4}>
                    <IconArrowRight title="arrow icon" />
                  </Box>
                </Button>
              </Box>
            </Box>
          </Grid.Item>
        </Grid>
      </Box>
    </>
  );
}

export default OptionalPaths;
